import React, { useLayoutEffect, useRef } from 'react';
import { render, nothing } from 'lit/html.js';
import { renderCheckoutPlusLegacy } from 'ui.shipaid.com/renderers/checkout-plus-legacy';
import type { CheckoutPlusLegacySnapshot, CheckoutPlusLegacyActions } from 'ui.shipaid.com/renderers/checkout-plus-legacy';

export interface LegacyCheckoutPreviewHostProps {
  readonly snapshot: CheckoutPlusLegacySnapshot;
  readonly actions?: CheckoutPlusLegacyActions;
  readonly testId?: string;
}
interface Lifecycle {
  node: HTMLDivElement | null;
  part: ReturnType<typeof render> | null;
  active: boolean;
}

export function LegacyCheckoutPreviewHost({ snapshot, actions, testId = 'renderer-host' }: LegacyCheckoutPreviewHostProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const lifecycleRef = useRef<Lifecycle>({ node: null, part: null, active: false });

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
    const node = document.createElement('div');
    root.append(node);
    const lifecycle: Lifecycle = { node, part: null, active: true };
    lifecycleRef.current = lifecycle;
    return () => {
      lifecycle.active = false;
      lifecycle.part?.setConnected(false);
      render(nothing, node);
      node.remove();
      lifecycleRef.current = { node: null, part: null, active: false };
    };
  }, []);

  useLayoutEffect(() => {
    const lifecycle = lifecycleRef.current;
    if (!lifecycle.active || !lifecycle.node) return;
    const previewAction = (callback?: (event: Event) => unknown) => (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (lifecycle.active) callback?.(event);
    };
    lifecycle.part = render(renderCheckoutPlusLegacy(snapshot, {
      onToggleDetails: previewAction(actions?.onToggleDetails),
      onSeeDetails: previewAction(actions?.onSeeDetails),
      onPrimaryClick: previewAction(actions?.onPrimaryClick),
      onSecondaryClick: previewAction(actions?.onSecondaryClick),
    }), lifecycle.node);
  }, [snapshot, actions]);

  return <div ref={hostRef} tabIndex={-1} data-testid={testId} role="group" aria-label="Sample Checkout+ legacy preview" />;
}
